import { type CallPopup, formatPhone } from "@nmt/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { get } from "../api";
import { BlockDialog } from "./block-dialog";
import { LeadDialog, LinkDialog } from "../pages/Comms";
import { useSession } from "../session";
import { cx } from "./ui";

const CLOSED_KEY = "nmt.closedCalls";
const readClosed = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(CLOSED_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
};

const HEAD: Record<CallPopup["state"], { label: string; className: string }> = {
  ringing: { label: "Incoming call", className: "bg-brand text-white" },
  on_call: { label: "On the call", className: "bg-ok text-white" },
  answered_elsewhere: { label: "Answered", className: "bg-ink-soft text-white" },
  missed: { label: "Missed call", className: "bg-wait text-white" },
};

/**
 * The incoming-call pop-up: when a call rings your RingCentral extension (your direct line, or the
 * main line's queue), it pops up in front of you with who's calling and their jobs. It checks every
 * few seconds while the app is open.
 */
export function CallPopups() {
  const { me } = useSession();
  const [closed, setClosed] = useState<string[]>(readClosed);
  const [dialog, setDialog] = useState<{ kind: "lead" | "link" | "block"; call: CallPopup } | null>(null);
  const calls = useQuery({
    queryKey: ["call-popups"],
    queryFn: () => get<CallPopup[]>("/api/comms/calls/popups"),
    enabled: !!me?.user.rcExtensionId,
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
  });
  const close = (id: string) => {
    const next = [id, ...closed].slice(0, 50);
    setClosed(next);
    try {
      localStorage.setItem(CLOSED_KEY, JSON.stringify(next));
    } catch {
      // Private browsing: closes for now only.
    }
  };
  // A ringing call goes on top.
  const rank = (c: CallPopup) => (c.state === "ringing" ? 0 : c.state === "on_call" ? 1 : 2);
  const shown = (calls.data ?? [])
    .filter((c) => !closed.includes(c.id))
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, 3);
  return (
    <>
      {shown.length > 0 && !dialog && (
        <div className="fixed inset-x-3 top-3 z-[70] flex flex-col gap-2 sm:left-auto sm:right-4 sm:w-[400px]" role="region" aria-label="Phone calls">
          {shown.map((c) => (
            <Card key={c.id} call={c} onClose={() => close(c.id)} onLead={() => setDialog({ kind: "lead", call: c })} onLink={() => setDialog({ kind: "link", call: c })} onBlock={() => setDialog({ kind: "block", call: c })} />
          ))}
        </div>
      )}
      {dialog?.kind === "lead" && <LeadDialog number={dialog.call.otherNumber} callerName={dialog.call.callerName} onClose={() => setDialog(null)} />}
      {dialog?.kind === "link" && <LinkDialog number={dialog.call.otherNumber} onClose={() => setDialog(null)} />}
      {dialog?.kind === "block" && <BlockDialog number={dialog.call.otherNumber} name={dialog.call.contacts[0]?.name ?? dialog.call.callerName} onClose={() => setDialog(null)} onBlocked={() => close(dialog.call.id)} />}
    </>
  );
}

/** "10:31 AM", and how long ago while it's recent. */
function callTime(startedAt: string, now = Date.now()) {
  const at = new Date(startedAt);
  const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const mins = Math.floor((now - at.getTime()) / 60_000);
  if (mins < 1) return `${time} · just now`;
  if (mins < 60) return `${time} · ${mins} min ago`;
  return time;
}

const seconds = (s: number | null) => (s == null ? "" : ` (${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")})`);

function Card({ call, onClose, onLead, onLink, onBlock }: { call: CallPopup; onClose: () => void; onLead: () => void; onLink: () => void; onBlock: () => void }) {
  const { can } = useSession();
  const head = HEAD[call.state];
  const known = call.contacts.length > 0;
  return (
    <div className="overflow-hidden rounded-[var(--radius-panel)] border border-line bg-white shadow-xl" role="alert">
      <div className={cx("flex items-center gap-2 px-4 py-2 text-sm font-semibold", head.className)}>
        {call.state === "ringing" && <span className="inline-block h-2.5 w-2.5 animate-ping rounded-full bg-white" aria-hidden />}
        <span>{call.state === "answered_elsewhere" ? `Answered by ${call.answeredBy}` : head.label}</span>
        {call.ourNumber && <span className="font-normal opacity-90">on {formatPhone(call.ourNumber)}</span>}
        <button className="ml-auto rounded px-1.5 text-lg leading-none opacity-90 hover:opacity-100" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="flex flex-col gap-2 px-4 py-3">
        {!known && <span className="text-lg font-semibold">{call.callerName ?? "Unknown caller"}</span>}
        {call.contacts.map((p) => (
          <div key={p.id} className="flex flex-col gap-1 text-sm">
            <Link to={`/contacts/${p.id}`} className="text-lg font-semibold hover:text-brand hover:underline">
              {p.name}
              <span className="ml-1.5 text-sm font-normal text-muted">{p.type}</span>
            </Link>
            {p.jobs.length ? (
              <ul className="flex flex-col gap-1">
                {p.jobs.map((j) => (
                  <li key={j.id}>
                    <Link to={`/jobs/${j.id}`} className={cx("block rounded-[var(--radius-control)] px-2 py-1 hover:bg-mist", !j.open && "text-muted")}>
                      <span className="font-medium">
                        #{j.number} {j.title}
                      </span>{" "}
                      <span className="text-xs">
                        · {j.stage}
                        {j.address ? ` · ${j.address}` : ""}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-muted">No jobs yet.</span>
            )}
          </div>
        ))}
        <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <a href={`tel:${call.otherNumber}`} className="text-brand hover:underline">
            {formatPhone(call.otherNumber)}
          </a>
          {known && call.callerName && <span className="text-xs text-muted">Caller ID: {call.callerName}</span>}
          <span className="ml-auto text-xs text-muted">{callTime(call.startedAt)}</span>
        </div>
        {call.voicemail && (
          <div className="flex flex-col gap-1 rounded-[var(--radius-control)] bg-mist px-2.5 py-2 text-sm">
            <span className="font-medium">Left a voicemail{seconds(call.voicemail.durationSeconds)}</span>
            <audio controls preload="none" src={`/api/comms/${call.voicemail.id}/audio`} className="h-8 w-full" />
            {call.voicemail.transcript && <p className="max-h-24 overflow-y-auto whitespace-pre-wrap text-ink-soft">{call.voicemail.transcript}</p>}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {!known && can("customers.edit") && (
            <>
              <button className="rounded-[var(--radius-control)] bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep" onClick={onLead}>
                New lead
              </button>
              <button className="rounded-[var(--radius-control)] border border-line px-3 py-1.5 text-sm font-semibold hover:bg-mist" onClick={onLink}>
                Link to a contact
              </button>
            </>
          )}
          {can("comms.view") && (
            <Link to={`/inbox?number=${encodeURIComponent(call.otherNumber)}`} onClick={onClose} className="rounded-[var(--radius-control)] border border-line px-3 py-1.5 text-sm font-semibold hover:bg-mist">
              Text
            </Link>
          )}
          {can("comms.send") && (
            <button className="ml-auto rounded-[var(--radius-control)] px-2 py-1.5 text-sm font-medium text-danger hover:bg-mist" onClick={onBlock}>
              Block
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
